import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

type DispositionCategory =
	| 'True Match'
	| 'False Positive'
	| 'Suspicious'
	| 'Closed';
type EscalationCategory =
	| 'Regulatory'
	| 'High Risk'
	| 'Management'
	| 'External';

type DispositionReason = {
	id: string;
	label: string;
	category: DispositionCategory;
	description: string;
	active: boolean;
};

type EscalationReason = {
	id: string;
	label: string;
	category: EscalationCategory;
	description: string;
	active: boolean;
};

const INITIAL_DISPOSITION: DispositionReason[] = [
	{
		id: 'dr_1',
		label: 'Confirmed Watchlist Match',
		category: 'True Match',
		description: 'Entity confirmed to be present on a regulatory watchlist.',
		active: true,
	},
	{
		id: 'dr_2',
		label: 'PEP Confirmed',
		category: 'True Match',
		description: 'Entity has been confirmed as a politically exposed person.',
		active: true,
	},
	{
		id: 'dr_3',
		label: 'Different Individual',
		category: 'False Positive',
		description:
			'Name match refers to a different individual after manual review.',
		active: true,
	},
	{
		id: 'dr_4',
		label: 'Insufficient Information',
		category: 'False Positive',
		description: 'Insufficient data to confirm the match as a true positive.',
		active: true,
	},
	{
		id: 'dr_5',
		label: 'Unusual Transaction Pattern',
		category: 'Suspicious',
		description:
			'Entity exhibits unusual behavior warranting further investigation.',
		active: true,
	},
	{
		id: 'dr_6',
		label: 'Adverse Media Found',
		category: 'Suspicious',
		description: 'Negative media coverage identified related to the entity.',
		active: false,
	},
	{
		id: 'dr_7',
		label: 'Case Resolved',
		category: 'Closed',
		description:
			'All investigation steps completed, no further action required.',
		active: true,
	},
	{
		id: 'dr_8',
		label: 'Duplicate Alert',
		category: 'Closed',
		description: 'Alert is a duplicate of an existing open alert or case.',
		active: true,
	},
];

const INITIAL_ESCALATION: EscalationReason[] = [
	{
		id: 'er_1',
		label: 'Regulatory Filing Required',
		category: 'Regulatory',
		description: 'Case requires STR/SAR filing with regulatory authority.',
		active: true,
	},
	{
		id: 'er_2',
		label: 'High-Risk Customer',
		category: 'High Risk',
		description: 'Customer risk rating warrants senior review.',
		active: true,
	},
	{
		id: 'er_3',
		label: 'Senior Management Review',
		category: 'Management',
		description: 'Case complexity requires senior management decision.',
		active: true,
	},
	{
		id: 'er_4',
		label: 'Law Enforcement Referral',
		category: 'External',
		description:
			'Evidence of criminal activity requiring law enforcement notification.',
		active: true,
	},
	{
		id: 'er_5',
		label: 'Cross-Border Complexity',
		category: 'Regulatory',
		description: 'Involves multiple jurisdictions requiring coordination.',
		active: false,
	},
];

const DISPOSITION_CATEGORIES: DispositionCategory[] = [
	'True Match',
	'False Positive',
	'Suspicious',
	'Closed',
];
const ESCALATION_CATEGORIES: EscalationCategory[] = [
	'Regulatory',
	'High Risk',
	'Management',
	'External',
];

function categoryBadge(cat: DispositionCategory | EscalationCategory) {
	switch (cat) {
		case 'True Match':
			return (
				<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
					True Match
				</Badge>
			);
		case 'False Positive':
			return (
				<Badge className="border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-50">
					False Positive
				</Badge>
			);
		case 'Suspicious':
			return (
				<Badge className="border border-orange-200 bg-orange-50 text-orange-700 text-xs hover:bg-orange-50">
					Suspicious
				</Badge>
			);
		case 'Closed':
			return (
				<Badge variant="secondary" className="text-xs">
					Closed
				</Badge>
			);
		case 'Regulatory':
			return (
				<Badge className="border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-50">
					Regulatory
				</Badge>
			);
		case 'High Risk':
			return (
				<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
					High Risk
				</Badge>
			);
		case 'Management':
			return (
				<Badge className="border border-purple-200 bg-purple-50 text-purple-700 text-xs hover:bg-purple-50">
					Management
				</Badge>
			);
		case 'External':
			return (
				<Badge className="border border-cyan-200 bg-cyan-50 text-cyan-700 text-xs hover:bg-cyan-50">
					External
				</Badge>
			);
	}
}

type EditForm = {
	label: string;
	description: string;
	category: string;
};

const EMPTY_FORM: EditForm = { label: '', description: '', category: '' };

export default function RationaleConfigPage() {
	const [dispositionReasons, setDispositionReasons] =
		useState<DispositionReason[]>(INITIAL_DISPOSITION);
	const [escalationReasons, setEscalationReasons] =
		useState<EscalationReason[]>(INITIAL_ESCALATION);
	const [activeTab, setActiveTab] = useState('disposition');
	const [editTarget, setEditTarget] = useState<string | null>(null);
	const [editForm, setEditForm] = useState<EditForm>(EMPTY_FORM);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [addForm, setAddForm] = useState<EditForm>(EMPTY_FORM);

	const handleToggleDisposition = (id: string) => {
		setDispositionReasons((prev) =>
			prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r)),
		);
	};

	const handleToggleEscalation = (id: string) => {
		setEscalationReasons((prev) =>
			prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r)),
		);
	};

	const handleDeleteDisposition = (id: string) => {
		setDispositionReasons((prev) => prev.filter((r) => r.id !== id));
		toast.success('Disposition reason deleted');
	};

	const handleDeleteEscalation = (id: string) => {
		setEscalationReasons((prev) => prev.filter((r) => r.id !== id));
		toast.success('Escalation reason deleted');
	};

	const openEditDialog = (
		id: string,
		label: string,
		description: string,
		category: string,
	) => {
		setEditTarget(id);
		setEditForm({ label, description, category });
		setEditDialogOpen(true);
	};

	const handleSaveEdit = () => {
		if (!editForm.label || !editForm.category) {
			toast.error('Label and category are required');
			return;
		}
		if (activeTab === 'disposition') {
			setDispositionReasons((prev) =>
				prev.map((r) =>
					r.id === editTarget
						? {
								...r,
								label: editForm.label,
								description: editForm.description,
								category: editForm.category as DispositionCategory,
							}
						: r,
				),
			);
		} else {
			setEscalationReasons((prev) =>
				prev.map((r) =>
					r.id === editTarget
						? {
								...r,
								label: editForm.label,
								description: editForm.description,
								category: editForm.category as EscalationCategory,
							}
						: r,
				),
			);
		}
		setEditDialogOpen(false);
		toast.success('Reason updated');
	};

	const handleAdd = () => {
		if (!addForm.label || !addForm.category) {
			toast.error('Label and category are required');
			return;
		}
		const newId = `${activeTab === 'disposition' ? 'dr' : 'er'}_${Date.now()}`;
		if (activeTab === 'disposition') {
			setDispositionReasons((prev) => [
				...prev,
				{
					id: newId,
					label: addForm.label,
					category: addForm.category as DispositionCategory,
					description: addForm.description,
					active: true,
				},
			]);
		} else {
			setEscalationReasons((prev) => [
				...prev,
				{
					id: newId,
					label: addForm.label,
					category: addForm.category as EscalationCategory,
					description: addForm.description,
					active: true,
				},
			]);
		}
		setAddForm(EMPTY_FORM);
		setAddDialogOpen(false);
		toast.success('Reason added');
	};

	const currentCategories =
		activeTab === 'disposition'
			? DISPOSITION_CATEGORIES
			: ESCALATION_CATEGORIES;

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-xl font-semibold text-[#0A0A0A]">
							Disposition Rationale Configuration
						</h1>
						<p className="mt-0.5 text-sm text-[#737373]">
							Configure reasons available for case dispositions and escalations
						</p>
					</div>
					<Button
						size="sm"
						className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
						onClick={() => {
							setAddForm(EMPTY_FORM);
							setAddDialogOpen(true);
						}}
					>
						<Plus className="mr-1.5 h-4 w-4" />
						Add Rationale
					</Button>
				</div>
			</div>

			<div className="p-6">
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="disposition">Disposition Reasons</TabsTrigger>
						<TabsTrigger value="escalation">Escalation Reasons</TabsTrigger>
					</TabsList>

					<TabsContent value="disposition" className="mt-4">
						<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Label
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Category
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Description
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Active
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Actions
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{dispositionReasons.map((reason) => (
										<TableRow key={reason.id}>
											<TableCell className="text-sm font-medium text-[#0a0a0a]">
												{reason.label}
											</TableCell>
											<TableCell>{categoryBadge(reason.category)}</TableCell>
											<TableCell className="text-sm text-[#525252] max-w-[280px]">
												{reason.description}
											</TableCell>
											<TableCell>
												<Switch
													checked={reason.active}
													onCheckedChange={() =>
														handleToggleDisposition(reason.id)
													}
												/>
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-1">
													<Button
														variant="ghost"
														size="sm"
														onClick={() =>
															openEditDialog(
																reason.id,
																reason.label,
																reason.description,
																reason.category,
															)
														}
													>
														<Pencil className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														className="text-red-600 hover:bg-red-50 hover:text-red-700"
														onClick={() => handleDeleteDisposition(reason.id)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</TabsContent>

					<TabsContent value="escalation" className="mt-4">
						<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Label
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Category
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Description
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Active
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Actions
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{escalationReasons.map((reason) => (
										<TableRow key={reason.id}>
											<TableCell className="text-sm font-medium text-[#0a0a0a]">
												{reason.label}
											</TableCell>
											<TableCell>{categoryBadge(reason.category)}</TableCell>
											<TableCell className="text-sm text-[#525252] max-w-[280px]">
												{reason.description}
											</TableCell>
											<TableCell>
												<Switch
													checked={reason.active}
													onCheckedChange={() =>
														handleToggleEscalation(reason.id)
													}
												/>
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-1">
													<Button
														variant="ghost"
														size="sm"
														onClick={() =>
															openEditDialog(
																reason.id,
																reason.label,
																reason.description,
																reason.category,
															)
														}
													>
														<Pencil className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														className="text-red-600 hover:bg-red-50 hover:text-red-700"
														onClick={() => handleDeleteEscalation(reason.id)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</TabsContent>
				</Tabs>
			</div>

			<Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Edit Reason</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-2">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="edit-label">
								Label <span className="text-red-500">*</span>
							</Label>
							<Input
								id="edit-label"
								value={editForm.label}
								onChange={(e) =>
									setEditForm((f) => ({ ...f, label: e.target.value }))
								}
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label>
								Category <span className="text-red-500">*</span>
							</Label>
							<Select
								value={editForm.category}
								onValueChange={(v) =>
									setEditForm((f) => ({ ...f, category: v }))
								}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select category" />
								</SelectTrigger>
								<SelectContent>
									{currentCategories.map((c) => (
										<SelectItem key={c} value={c}>
											{c}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="edit-desc">Description</Label>
							<Textarea
								id="edit-desc"
								rows={3}
								value={editForm.description}
								onChange={(e) =>
									setEditForm((f) => ({ ...f, description: e.target.value }))
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
							onClick={handleSaveEdit}
						>
							Save
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle>
							Add {activeTab === 'disposition' ? 'Disposition' : 'Escalation'}{' '}
							Reason
						</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-2">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="add-label">
								Label <span className="text-red-500">*</span>
							</Label>
							<Input
								id="add-label"
								placeholder="Reason label"
								value={addForm.label}
								onChange={(e) =>
									setAddForm((f) => ({ ...f, label: e.target.value }))
								}
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label>
								Category <span className="text-red-500">*</span>
							</Label>
							<Select
								value={addForm.category}
								onValueChange={(v) =>
									setAddForm((f) => ({ ...f, category: v }))
								}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select category" />
								</SelectTrigger>
								<SelectContent>
									{currentCategories.map((c) => (
										<SelectItem key={c} value={c}>
											{c}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="add-desc">Description</Label>
							<Textarea
								id="add-desc"
								placeholder="Brief description of when to use this reason..."
								rows={3}
								value={addForm.description}
								onChange={(e) =>
									setAddForm((f) => ({ ...f, description: e.target.value }))
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setAddDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
							onClick={handleAdd}
						>
							Add Reason
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
